import { useState, useCallback, useRef } from 'react';
import { useUser } from './useUser';
import { useWorkspaces } from './useWorkspaces';
import { apiFetch } from '@/lib/apiFetch';

interface ToolExecutionOptions {
  toolId: string;
  input: Record<string, unknown>;
  workspaceId?: string;
}

interface ExecutionResult {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  output?: unknown;
  error?: string;
  created_at: string;
  completed_at?: string;
}

interface StreamChunk {
  type: 'token' | 'result' | 'error' | 'metadata';
  content: unknown;
}

export function useToolExecution() {
  const userContext = useUser();
  const workspaceContext = useWorkspaces();
  const user = userContext?.user || null;
  const currentWorkspace = workspaceContext?.currentWorkspace || null;
  const [executing, setExecuting] = useState(false);
  const [streamingOutput, setStreamingOutput] = useState<string>('');
  const [result, setResult] = useState<ExecutionResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const cancelRef = useRef(false);

  /** Network-class failures only — never retry on a user cancel or API errors. */
  function isRetryableNetworkError(err: unknown): boolean {
    if (cancelRef.current) return false;
    const msg = String(err instanceof Error ? err.message : err);
    return (
      err instanceof TypeError ||
      /failed to fetch|fetch failed|networkerror|econnreset|socket hang up/i.test(
        msg
      )
    );
  }

  const executeTool = useCallback(
    async ({ toolId, input, workspaceId }: ToolExecutionOptions) => {
      console.log('[useToolExecution] START', {
        toolId,
        hasUser: !!user,
        input,
      });

      if (!user) {
        console.error('[useToolExecution] No user - cannot execute');
        setError(new Error('User not authenticated'));
        return null;
      }

      const targetWorkspaceId = workspaceId || currentWorkspace?.id;
      console.log('[useToolExecution] Using workspace:', targetWorkspaceId);

      setExecuting(true);
      setError(null);
      setStreamingOutput('');
      setResult(null);
      cancelRef.current = false;

      for (let attemptsLeft = 1; attemptsLeft >= 0; attemptsLeft--) {
        // Create abort controller for cancellation
        abortControllerRef.current = new AbortController();
        const cleanup = () => {
          setExecuting(false);
          abortControllerRef.current = null;
        };

        try {
          console.log('[useToolExecution] Calling apiFetch...');
          const response = await apiFetch(`/api/tools/${toolId}/execute`, {
            method: 'POST',
            signal: abortControllerRef.current.signal,
            body: JSON.stringify({
              input,
              ...(targetWorkspaceId ? { workspace_id: targetWorkspaceId } : {}),
            }),
          });

          console.log('[useToolExecution] Response received', {
            status: response.status,
            ok: response.ok,
            contentType: response.headers.get('content-type'),
          });

          if (!response.ok) {
            // Check if response is JSON before parsing
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errorData = await response.json();
              console.error('[useToolExecution] API error:', errorData);
              throw new Error(errorData.error || 'Tool execution failed');
            } else {
              const text = await response.text();
              console.error(
                '[useToolExecution] Non-JSON error response:',
                text
              );
              throw new Error('API endpoint not available');
            }
          }

          // Check if response is streaming
          const contentType = response.headers.get('content-type');
          if (
            contentType?.includes('text/event-stream') ||
            contentType?.includes('application/x-ndjson')
          ) {
            // Handle streaming response
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) {
              throw new Error('Response body is not readable');
            }

            let accumulatedOutput = '';

            while (true) {
              const { done, value } = await reader.read();

              if (done) {
                break;
              }

              const chunk = decoder.decode(value, { stream: true });
              const lines = chunk.split('\n').filter((line) => line.trim());

              for (const line of lines) {
                try {
                  // Handle SSE format (data: {...})
                  const jsonStr = line.startsWith('data: ')
                    ? line.slice(6)
                    : line;

                  if (jsonStr === '[DONE]') {
                    continue;
                  }

                  const data: StreamChunk = JSON.parse(jsonStr);

                  if (data.type === 'token') {
                    accumulatedOutput += data.content;
                    setStreamingOutput(accumulatedOutput);
                  } else if (data.type === 'result') {
                    setResult(data.content as ExecutionResult);
                  } else if (data.type === 'error') {
                    throw new Error(String(data.content));
                  } else if (data.type === 'metadata') {
                    // Handle metadata if needed
                    console.log('Execution metadata:', data.content);
                  }
                } catch (parseError) {
                  console.warn(
                    'Failed to parse stream chunk:',
                    line,
                    parseError
                  );
                }
              }
            }

            cleanup();
            return result;
          } else {
            // Handle non-streaming response
            const data = await response.json();
            console.log(
              '[useToolExecution] Non-streaming response data:',
              data
            );
            setResult(data);
            cleanup();
            return data;
          }
        } catch (err) {
          if (attemptsLeft > 0 && isRetryableNetworkError(err)) {
            console.warn(
              '[useToolExecution] Network failure — retrying once:',
              String(err)
            );
            setStreamingOutput('');
            continue;
          }

          if (err instanceof Error) {
            if (err.name === 'AbortError') {
              setError(
                new Error(
                  cancelRef.current
                    ? 'Execution cancelled'
                    : 'Request timed out'
                )
              );
            } else {
              setError(err);
            }
          } else {
            setError(new Error('Unknown error during execution'));
          }
          cleanup();
          return null;
        }
      }
    },
    [user, currentWorkspace, result]
  );

  const cancelExecution = useCallback(() => {
    cancelRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const resetExecution = useCallback(() => {
    cancelRef.current = false;
    setExecuting(false);
    setStreamingOutput('');
    setResult(null);
    setError(null);
    abortControllerRef.current = null;
  }, []);

  return {
    executing,
    streamingOutput,
    result,
    error,
    setError,
    executeTool,
    cancelExecution,
    resetExecution,
  };
}
